import { Input } from "@/components/ui/input";
import { listBrandRecords } from "@/lib/catalog/brandsStore";
import { listCategories } from "@/lib/catalog/mockCatalogApi";
import { computeLandedCost } from "@/lib/catalog/purchaseCost";
import { listProducts, type ProductRecord } from "@/lib/catalog/productsStore";
import type { Category } from "@/lib/catalog/types";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { Car, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const MAX_RESULTS = 8;

function formatPKR(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return `₨ ${Number(n).toLocaleString()}`;
}

export function InventorySearch() {
  const navigate = useNavigate();
  const [authUser, setAuthUser] = useState(() => getStoredAuthUser());
  const canViewAny = can(authUser, "product_can_view_any");
  const canViewDetail = can(authUser, "product_can_view");
  const canViewPrices = can(authUser, "product_can_view_prices");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setAuthUser(getStoredAuthUser());
    };
    const onAuth = () => setAuthUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const categoryNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of categories) m.set(c.id, c.name);
    return m;
  }, [categories]);

  const brandNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of listBrandRecords()) m.set(b.id, b.name);
    return m;
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !canViewAny) return [];
    const products = listProducts();
    return products
      .filter((p) => {
        const brand = p.brandId ? brandNameById.get(p.brandId) ?? "" : "";
        const cat = categoryNameById.get(p.categoryId) ?? "";
        return (
          p.name.toLowerCase().includes(q) ||
          p.slug.toLowerCase().includes(q) ||
          cat.toLowerCase().includes(q) ||
          brand.toLowerCase().includes(q)
        );
      })
      .slice(0, MAX_RESULTS);
  }, [query, categoryNameById, brandNameById, canViewAny]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = useCallback(
    (p: ProductRecord) => {
      if (!canViewDetail) {
        toast.error("You don’t have permission to open product details.");
        return;
      }
      navigate(`/products/${p.id}`);
      setQuery("");
      setOpen(false);
    },
    [navigate, canViewDetail],
  );

  const showPanel = open && query.trim().length > 0;

  return (
    <div ref={containerRef} className="relative w-64">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        placeholder="Search inventory..."
        className="glass-input h-9 w-full pl-9 text-sm"
        value={query}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showPanel}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
      />
      {showPanel ? (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[min(24rem,70vh)] overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md"
        >
          {results.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">No products found</div>
          ) : (
            <ul className="p-1">
              {results.map((p) => {
                const img = p.productImages?.[0]?.url;
                const cat = categoryNameById.get(p.categoryId) ?? "—";
                const brandLabel = p.brandId ? brandNameById.get(p.brandId) : null;
                const subtitle = [cat, brandLabel].filter(Boolean).join(" · ");
                const landed = p.landedCost ?? computeLandedCost(p.purchasePrice, p.purchaseCostLines);
                const price = landed ?? p.purchasePrice ?? null;
                const status = p.inventoryStatus ?? "in_stock";
                const statusLabel =
                  status === "sold" ? "Sold" : status === "reserved" ? "Reserved" : "In stock";
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      role="option"
                      className="flex w-full items-center gap-3 rounded-sm px-2 py-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pick(p)}
                    >
                      <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md border border-border/60 bg-muted">
                        {img ? (
                          <img src={img} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <Car className="h-6 w-6 text-muted-foreground/50" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-foreground">{p.name}</p>
                        {subtitle ? (
                          <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
                        ) : null}
                        <div className="mt-0.5 flex flex-wrap items-center gap-2">
                          <span className="text-xs font-semibold text-primary">
                            {canViewPrices ? (
                              p.purchaseCurrency && p.purchaseCurrency !== "PKR"
                                ? `${p.purchaseCurrency} ${price != null ? Number(price).toLocaleString() : "—"}`
                                : formatPKR(price)
                            ) : (
                              <span className="text-muted-foreground font-normal">—</span>
                            )}
                          </span>
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                            {statusLabel}
                          </span>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
